import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Truck, Check } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, SERVICE_OPTIONS } from "@/data/site";
import { Reveal, MaskLineInView } from "@/components/site/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full bg-background border border-border px-4 py-3.5 text-base outline-none focus:border-foreground focus:ring-1 focus:ring-ring transition-colors duration-200 placeholder:text-muted-foreground";
const label = "text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 block";

export const BookSection = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", truck_info: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Please add your name, phone and email.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setDone(true);
      toast.success("Request sent — Dylan will reach out shortly.");
    } catch {
      toast.error("Something went wrong. Please call us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" className="bg-background pt-24 md:pt-32 scroll-mt-16" data-testid="book">
      <div className="mx-auto max-w-[1400px] px-6 md:px-8">
        <Reveal>
          <span className="text-xs tracking-[0.35em] uppercase text-muted-foreground">Book Your Truck</span>
        </Reveal>
        <h2 className="font-display uppercase tracking-tighter leading-[0.86] text-[13vw] md:text-[9vw] lg:text-[8rem] mt-4">
          <MaskLineInView>Let's make</MaskLineInView>
          <MaskLineInView delay={0.08} className="text-outline">it shine.</MaskLineInView>
        </h2>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-px bg-border border border-border">
          {/* Contact rail */}
          <div className="lg:col-span-5 bg-background p-8 md:p-12 flex flex-col justify-between gap-10">
            <div className="space-y-8">
              <a data-testid="book-call" href={CONTACT.phoneHref} className="group block">
                <span className={label}>Call / Text</span>
                <span className="flex items-center gap-3 font-display text-2xl md:text-3xl group-hover:text-outline transition-colors duration-300">
                  <Phone size={22} /> {CONTACT.phone}
                </span>
              </a>
              <a data-testid="book-email" href={CONTACT.emailHref} className="group block">
                <span className={label}>Email</span>
                <span className="flex items-center gap-3 font-display text-lg md:text-2xl break-all group-hover:text-outline transition-colors duration-300">
                  <Mail size={20} /> {CONTACT.email}
                </span>
              </a>
              <div>
                <span className={label}>Service Area</span>
                <span className="flex items-start gap-3 text-lg text-muted-foreground">
                  <MapPin size={20} className="mt-1 shrink-0" /> {CONTACT.region}
                </span>
              </div>
              <div>
                <span className={label}>How We Work</span>
                <span className="flex items-start gap-3 text-lg text-muted-foreground">
                  <Truck size={20} className="mt-1 shrink-0" /> {CONTACT.mobile}
                </span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              Prefer to talk it through? Call or text us directly — happy to build a custom quote for your unit.
            </p>
          </div>

          {/* Form */}
          <div className="lg:col-span-7 bg-background p-8 md:p-12">
            {done ? (
              <div data-testid="book-success" className="h-full min-h-[320px] flex flex-col items-center justify-center text-center gap-5">
                <div className="w-16 h-16 border border-foreground flex items-center justify-center">
                  <Check size={30} />
                </div>
                <h3 className="font-display uppercase tracking-tight text-3xl">Request Sent</h3>
                <p className="text-muted-foreground max-w-sm">
                  Thanks {form.name.split(" ")[0] || "there"} — we've got your details. Dylan will reach out to lock in
                  a time. Need it sooner? Call {CONTACT.phone}.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="booking-form">
                <div>
                  <label className={label}>Name *</label>
                  <input data-testid="input-name" className={field} value={form.name} onChange={set("name")} placeholder="Your name" />
                </div>
                <div>
                  <label className={label}>Phone *</label>
                  <input data-testid="input-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="Best number to reach you" />
                </div>
                <div className="sm:col-span-2">
                  <label className={label}>Email *</label>
                  <input data-testid="input-email" type="email" className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                </div>
                <div>
                  <label className={label}>Truck / Unit</label>
                  <input data-testid="input-truck" className={field} value={form.truck_info} onChange={set("truck_info")} placeholder="Make, model, # of units" />
                </div>
                <div>
                  <label className={label}>Service</label>
                  <select data-testid="input-service" className={field} value={form.service} onChange={set("service")}>
                    <option value="">Select a service</option>
                    {SERVICE_OPTIONS.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className={label}>Details</label>
                  <textarea data-testid="input-message" rows={4} className={field} value={form.message} onChange={set("message")} placeholder="Tell us about the aluminum, timing, location…" />
                </div>
                <button
                  data-testid="booking-submit"
                  type="submit"
                  disabled={loading}
                  className="sm:col-span-2 bg-foreground text-background px-8 py-4 text-sm tracking-[0.2em] uppercase border border-foreground hover:bg-transparent hover:text-foreground transition-colors duration-300 disabled:opacity-50"
                >
                  {loading ? "Sending…" : "Request Booking"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
